"use client";

import { motion } from "framer-motion";
import { BookOpen, Heart, Globe2, Crown } from "lucide-react";

const pillars = [
  {
    icon: BookOpen,
    title: "Le plus savant",
    text: "Chaque racine porte en elle des siècles de savoir agronomique, de traditions paysannes et de science nutritionnelle. Nous cultivons l'excellence par la connaissance.",
  },
  {
    icon: Crown,
    title: "Le plus élégant",
    text: "De la parcelle à l'assiette, nous soignons chaque geste : récolte au bon moment, tri minutieux, présentation soignée. Nos produits méritent leur lettre de noblesse.",
  },
  {
    icon: Heart,
    title: "Le plus nourricier",
    text: "Patates douces, pommes de terre, manioc, igname : des trésors de fibres, de vitamines et d'énergie. La nourriture la plus complète, la plus généreuse.",
  },
  {
    icon: Globe2,
    title: "Le plus patriote",
    text: "Nos terres, nos paysans, notre fierté. Nous célébrons le patrimoine agricole local et contribuons à la souveraineté alimentaire de notre nation.",
  },
];

export function About() {
  return (
    <section id="histoire" className="relative py-24 lg:py-32 overflow-hidden">
      {/* Soft decorative background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 right-0 w-96 h-96 rounded-full blur-3xl opacity-30"
             style={{ background: "radial-gradient(circle, oklch(0.86 0.13 80 / 0.4), transparent 70%)" }} />
        <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full blur-3xl opacity-30"
             style={{ background: "radial-gradient(circle, oklch(0.55 0.13 135 / 0.3), transparent 70%)" }} />
      </div>

      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="divider-gold max-w-xs mx-auto mb-5"
          >
            <span className="text-xs uppercase tracking-[0.32em] text-gold-deep font-semibold">
              Notre Histoire
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-foreground"
          >
            Une Maison bâtie sur la{" "}
            <span className="text-gradient-gold">terre</span> et l&apos;
            <span className="text-gradient-gold">or</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 text-base sm:text-lg text-foreground/70 leading-relaxed"
          >
            PATATE OR naît d&apos;une conviction simple : la terre nourrit ceux qui la
            respectent. De la patate douce dorée à l&apos;igname royal, du manioc
            généreux à la pomme de terre familière, nous transformons chaque racine en
            promesse de qualité. Notre maison réunit producteurs, transformateurs et
            gourmets autour d&apos;une même exigence : celle de l&apos;authenticité.
          </motion.p>
        </div>

        {/* Pillars grid */}
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {pillars.map((p, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="group relative p-6 rounded-2xl bg-card border border-border hover:border-gold hover:shadow-gold transition-all duration-500"
            >
              <div className="mb-5 grid place-items-center w-14 h-14 rounded-xl bg-gradient-gold shadow-gold group-hover:scale-110 transition-transform">
                <p.icon className="w-7 h-7 text-white" strokeWidth={2} />
              </div>
              <h3 className="font-display text-xl font-bold text-foreground mb-2">
                {p.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {p.text}
              </p>
              <div className="mt-4 h-px w-full bg-gradient-to-r from-gold/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </motion.div>
          ))}
        </div>

        {/* Manifesto block */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="mt-20 relative rounded-3xl overflow-hidden"
        >
          <div className="absolute inset-0">
            <img
              src="/images/farm-field.png"
              alt="Champ fertile au coucher du soleil"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-earth-deep/95 via-earth-deep/80 to-earth-deep/40" />
          </div>
          <div className="relative px-6 sm:px-12 lg:px-20 py-16 lg:py-24 max-w-3xl">
            <span className="text-xs uppercase tracking-[0.32em] text-gold-soft font-semibold">
              Notre Manifeste
            </span>
            <p className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-cream leading-snug">
              « Cultiver la racine, c&apos;est cultiver la dignité. La transformer,
              c&apos;est honorer le travail. La partager, c&apos;est bâtir une nation
              nourrie et fière. »
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3 text-cream/80">
              <div className="h-px w-12 bg-gold" />
              <span className="text-sm font-medium tracking-wide">La Maison PATATE OR — Ndiamsyl Peye, Smartest Capital</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
